import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  MagnifyingGlass, 
  Download, 
  Pencil, 
  Trash, 
  CaretLeft,
  CaretRight,
  Funnel,
  X,
  Student,
  IdentificationCard
} from '@phosphor-icons/react';
import { studentsApi, programsApi, formatApiErrorDetail } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const studentSchema = z.object({
  registrationNumber: z.string().min(1, 'Required'),
  dateOfBirth: z.string().min(1, 'Required'),
  studentName: z.string().min(1, 'Required'),
  programCode: z.string().min(1, 'Required'),
});

export default function ViewStudentsPage() {
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [editStudent, setEditStudent] = useState(null);
  const [deleteStudent, setDeleteStudent] = useState(null);
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(studentSchema),
  });

  useEffect(() => { loadPrograms(); }, []);
  useEffect(() => { loadStudents(); }, [pagination.page, searchText, programFilter]);

  const loadPrograms = async () => {
    try {
      const { data } = await programsApi.getAll();
      setPrograms(data);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit };
      if (searchText.trim()) {
        params.search = searchText.trim();
      }
      if (programFilter) params.programCode = programFilter;
      const { data } = await studentsApi.getAll(params);
      setStudents(data.data);
      setPagination(prev => ({ ...prev, total: data.total, totalPages: data.totalPages }));
    } catch (error) {
      console.error('Failed to load students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadStudents();
  };

  const clearFilters = () => {
    setSearchText('');
    setProgramFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      const response = await studentsApi.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'students.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(dateStr)) {
      const parts = dateStr.split(/[\/\-]/);
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  const formatDateForStorage = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };

  const openEditModal = (student) => {
    setEditStudent(student);
    const formattedStudent = { ...student, dateOfBirth: formatDateForInput(student.dateOfBirth) };
    reset(formattedStudent);
  };

  const handleEdit = async (data) => {
    setSaving(true);
    try {
      const formattedData = { ...data, dateOfBirth: formatDateForStorage(data.dateOfBirth) };
      await studentsApi.update(editStudent.id, formattedData);
      toast.success('Student updated successfully');
      setEditStudent(null);
      loadStudents();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await studentsApi.delete(deleteStudent.id);
      toast.success('Student deleted');
      setDeleteStudent(null);
      loadStudents();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const { data } = await studentsApi.deleteAll();
      toast.success(data?.message || `All students deleted`);
      setShowDeleteAll(false);
      loadStudents();
    } catch (error) {
      toast.error('Failed to delete all students');
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const d = formatDateForInput(dateStr);
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split('-');
      return `${day}/${m}/${y}`;
    }
    return dateStr;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="overline mb-2">Student Management</p>
          <h1 className="text-3xl font-black tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            View Students
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowDeleteAll(true)} variant="outline" data-testid="delete-all-students-btn"
            className="rounded-none border-[#E31E25] text-[#E31E25] hover:bg-[#FEF2F2]">
            <Trash size={18} className="mr-2" />
            Delete All
          </Button>
          <Button onClick={handleExport} data-testid="export-students-btn" className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none">
            <Download size={18} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="student-search-form">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by Reg. No. or Name..."
            className="pl-10 rounded-none border-[#E8E8E8] h-10"
            data-testid="student-search-input"
          />
        </div>
        <Select value={programFilter} onValueChange={(val) => { setProgramFilter(val); setPagination(prev => ({ ...prev, page: 1 })); }}>
          <SelectTrigger className="rounded-none border-[#E8E8E8] w-full sm:w-[220px] h-10" data-testid="student-program-filter">
            <SelectValue placeholder="All Programs" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-white border-[#E8E8E8] max-h-60">
            {programs.map((p) => (
              <SelectItem key={p.programCode} value={p.programCode}>
                {p.programCode} - {p.programName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none h-10 px-6" data-testid="student-search-btn">
          Search
        </Button>
        {(searchText || programFilter) && (
          <Button type="button" variant="outline" onClick={clearFilters} className="rounded-none h-10 px-4" data-testid="student-clear-filters-btn">
            <X size={16} className="mr-1" /> Clear
          </Button>
        )}
      </form>

      {/* Table */}
      <div className="bg-white border border-[#E8E8E8] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="students-table">
            <thead>
              <tr className="bg-[#FEF7F7] border-b border-[#E8E8E8]">
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#1A1A1A]">Reg. No.</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#1A1A1A]">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#1A1A1A]">DOB</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#1A1A1A]">Program</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-[#1A1A1A]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-[#E8E8E8]">
                    {[...Array(5)].map((_, j) => (
                      <td key={j} className="py-3 px-4"><div className="h-4 bg-[#E8E8E8] animate-pulse w-20" /></td>
                    ))}
                  </tr>
                ))
              ) : students.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center text-[#999]">
                    <Student size={40} className="mx-auto mb-3 text-[#ccc]" />
                    <p className="font-medium text-[#666]">No students found</p>
                    <p className="text-sm mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr key={student.id} className="border-b border-[#E8E8E8] hover:bg-[#FAFAFA] transition-colors">
                    <td className="py-3 px-4 text-sm font-mono font-medium text-[#333]">{student.registrationNumber}</td>
                    <td className="py-3 px-4 text-sm text-[#333]">{student.studentName}</td>
                    <td className="py-3 px-4 text-sm text-[#666]">{formatDisplayDate(student.dateOfBirth)}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className="bg-[#FEF7F7] px-2 py-1 text-xs font-medium border border-[#E8E8E8]">
                        {student.programCode}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEditModal(student)} data-testid={`edit-student-${student.id}`}
                          className="p-2 hover:bg-[#E31E25]/10 text-[#999] hover:text-[#E31E25] transition-colors rounded">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => setDeleteStudent(student)} data-testid={`delete-student-${student.id}`}
                          className="p-2 hover:bg-[#E31E25]/10 text-[#999] hover:text-[#E31E25] transition-colors rounded">
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-[#E8E8E8] bg-[#FAFAFA]">
            <p className="text-sm text-[#666]">
              {pagination.total > 0 ? `Showing ${((pagination.page - 1) * pagination.limit) + 1}-${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}` : '0 results'}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} className="rounded-none h-8 w-8 p-0">
                <CaretLeft size={16} />
              </Button>
              <span className="text-sm font-medium text-[#333] min-w-[60px] text-center">
                {pagination.page} / {pagination.totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} className="rounded-none h-8 w-8 p-0">
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Student Modal */}
      <Dialog open={!!editStudent} onOpenChange={() => setEditStudent(null)}>
        <DialogContent className="sm:max-w-[480px] p-0 gap-0 bg-white border-0 shadow-2xl rounded-none overflow-hidden" data-testid="edit-student-modal" aria-describedby="edit-student-desc">
          {/* Modal Header */}
          <div className="bg-[#E31E25] px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-white text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                <IdentificationCard size={22} weight="bold" />
                Edit Student
              </DialogTitle>
              <p className="text-white/70 text-sm mt-1" id="edit-student-desc">Update student information</p>
            </DialogHeader>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit(handleEdit)} className="p-6 space-y-5">
            <div>
              <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Registration Number</Label>
              <Input {...register('registrationNumber')}
                className="mt-1.5 h-11 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25] font-mono"
                data-testid="edit-student-reg-input" />
              {errors.registrationNumber && <p className="text-[#E31E25] text-xs mt-1">{errors.registrationNumber.message}</p>}
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Student Name</Label>
              <Input {...register('studentName')}
                className="mt-1.5 h-11 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25]"
                data-testid="edit-student-name-input" />
              {errors.studentName && <p className="text-[#E31E25] text-xs mt-1">{errors.studentName.message}</p>}
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Date of Birth</Label>
              <Input type="date" {...register('dateOfBirth')}
                className="mt-1.5 h-11 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white focus:border-[#E31E25] focus:ring-1 focus:ring-[#E31E25]"
                max="2010-12-31" min="1980-01-01"
                data-testid="edit-student-dob-input" />
              {errors.dateOfBirth && <p className="text-[#E31E25] text-xs mt-1">{errors.dateOfBirth.message}</p>}
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#666] uppercase tracking-wider">Program</Label>
              <Select defaultValue={editStudent?.programCode} onValueChange={(value) => setValue('programCode', value)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-none border-[#E0E0E0] bg-[#FAFAFA] focus:bg-white" data-testid="edit-student-program-select">
                  <SelectValue placeholder="Select Program" />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-white border-[#E0E0E0] max-h-60">
                  {programs.map((p) => (
                    <SelectItem key={p.programCode} value={p.programCode}>
                      {p.programCode} - {p.programName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.programCode && <p className="text-[#E31E25] text-xs mt-1">{errors.programCode.message}</p>}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#F0F0F0]">
              <Button type="button" variant="outline" onClick={() => setEditStudent(null)}
                className="rounded-none border-[#E0E0E0] hover:bg-[#F5F5F5] px-6 h-10" data-testid="edit-student-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={saving}
                className="bg-[#E31E25] hover:bg-[#C41A20] text-white rounded-none px-6 h-10" data-testid="edit-student-save-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteStudent} onOpenChange={() => setDeleteStudent(null)}>
        <AlertDialogContent className="rounded-none border-0 shadow-2xl p-0 overflow-hidden" data-testid="delete-student-dialog">
          <div className="bg-[#FEF2F2] px-6 py-4 border-b border-[#FCA5A5]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#991B1B] font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Delete Student
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#B91C1C] text-sm">
                Are you sure you want to delete <strong>{deleteStudent?.studentName}</strong> ({deleteStudent?.registrationNumber})? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="px-6 py-4">
            <AlertDialogCancel className="rounded-none" data-testid="delete-student-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}
              className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none" data-testid="delete-student-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Students Confirmation */}
      <AlertDialog open={showDeleteAll} onOpenChange={setShowDeleteAll}>
        <AlertDialogContent className="rounded-none border-0 shadow-2xl p-0 overflow-hidden" data-testid="delete-all-students-dialog">
          <div className="bg-[#FEF2F2] px-6 py-4 border-b border-[#FCA5A5]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#991B1B] font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Delete All Students
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#B91C1C] text-sm">
                This will permanently delete <strong>all {pagination.total} students</strong> from the database. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="px-6 py-4">
            <AlertDialogCancel className="rounded-none" data-testid="delete-all-students-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAll}
              className="bg-[#E31E25] hover:bg-[#C41A20] rounded-none" data-testid="delete-all-students-confirm-btn">
              Delete All Students
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
